import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LoadingState } from "@/components/States";
import { LaunchSplash } from "@/components/brand/LaunchSplash";

// Login is eager (first paint); everything else is code-split per route.
import Login from "@/pages/Login";
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const PortalInviteRedeem = lazy(() => import("@/pages/PortalInviteRedeem"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PolicyAnalysisDetail = lazy(() => import("@/pages/PolicyAnalysisDetail"));
// Consolidated hubs (Phase 1-3 IA merge): Studio wraps Ask + AI Studio + Meetings + Policy Analyzer;
// Insights wraps Analytics + Radar + Forecast + Autonomous + Firm DNA; KnowledgeHub wraps Reuse +
// Review Cockpit + Knowledge Base + Content Library + Master Documents; ComplianceReference wraps
// Control Library + Frameworks + Regulations. Each hub renders the underlying page unchanged;
// legacy top-level routes below redirect into the hub with ?tab= (query params preserved).
const Studio = lazy(() => import("@/pages/Studio"));
const Insights = lazy(() => import("@/pages/Insights"));
const KnowledgeHub = lazy(() => import("@/pages/KnowledgeHub"));
const ComplianceReference = lazy(() => import("@/pages/ComplianceReference"));
const OutputDetail = lazy(() => import("@/pages/OutputDetail"));
const Workspaces = lazy(() => import("@/pages/Workspaces"));
const WorkspaceDetail = lazy(() => import("@/pages/WorkspaceDetail"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ControlDetail = lazy(() => import("@/pages/ControlDetail"));
const FrameworkDetail = lazy(() => import("@/pages/FrameworkDetail"));
const IntelligenceClients = lazy(() => import("@/pages/IntelligenceClients"));
const CompanyProfile = lazy(() => import("@/pages/CompanyProfile"));
const AdminConsole = lazy(() => import("@/pages/AdminConsole"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PublicScopingForm = lazy(() => import("@/pages/PublicScopingForm"));
const PublicPosture = lazy(() => import("@/pages/PublicPosture"));
const MfaChallenge = lazy(() => import("@/pages/MfaChallenge"));
const MfaSetup = lazy(() => import("@/pages/MfaSetup"));

// Redirects a legacy list route into a hub tab, preserving any query params (?id=, ?type= …).
function HubRedirect({ to, tab }) {
  const [params] = useSearchParams();
  const n = new URLSearchParams(params);
  n.set("tab", tab);
  return <Navigate to={`${to}?${n.toString()}`} replace />;
}

function App() {
  return (
    <div className="App">
      <LaunchSplash />
      <Toaster position="top-right" richColors closeButton />
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <Suspense fallback={<LoadingState label="Loading" />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/portal/login" element={<Login portal />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* MFA — challenge is public (uses one-shot token from login), setup requires an active session */}
                <Route path="/mfa/challenge" element={<MfaChallenge />} />
                <Route path="/mfa/setup" element={<ProtectedRoute allowMfaPending><MfaSetup /></ProtectedRoute>} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/portal-invite" element={<PortalInviteRedeem />} />
                <Route path="/scope/:token" element={<PublicScopingForm />} />
                <Route path="/posture/:token" element={<PublicPosture />} />
                <Route path="/" element={<ProtectedRoute perm="dashboard:view"><Dashboard /></ProtectedRoute>} />
                {/* Knowledge Hub — consolidates Reuse · Review Cockpit · Knowledge · Content · Master Documents */}
                <Route path="/knowledge" element={<ProtectedRoute perm="dashboard:view"><KnowledgeHub /></ProtectedRoute>} />
                <Route path="/library" element={<HubRedirect to="/knowledge" tab="content" />} />
                <Route path="/reuse" element={<HubRedirect to="/knowledge" tab="reuse" />} />
                <Route path="/outputs" element={<HubRedirect to="/knowledge" tab="review" />} />
                <Route path="/master-documents" element={<HubRedirect to="/knowledge" tab="master" />} />
                {/* Compliance Reference — consolidates Controls · Frameworks · Regulations */}
                <Route path="/compliance-ref" element={<ProtectedRoute perm="dashboard:view"><ComplianceReference /></ProtectedRoute>} />
                <Route path="/control-library" element={<HubRedirect to="/compliance-ref" tab="controls" />} />
                <Route path="/frameworks" element={<HubRedirect to="/compliance-ref" tab="frameworks" />} />
                <Route path="/regulations" element={<HubRedirect to="/compliance-ref" tab="regulations" />} />
                {/* Insights hub — consolidates Analytics · Radar · Forecast · Autonomous · Firm DNA */}
                <Route path="/insights" element={<ProtectedRoute perm="reports:view"><Insights /></ProtectedRoute>} />
                <Route path="/analytics" element={<Navigate to="/insights?tab=analytics" replace />} />
                <Route path="/command-center" element={<Navigate to="/" replace />} />
                <Route path="/autonomous" element={<Navigate to="/insights?tab=autonomous" replace />} />
                <Route path="/meetings" element={<Navigate to="/studio?assistant=meetings" replace />} />
                <Route path="/policy-analyzer" element={<Navigate to="/studio?assistant=grc&view=policy" replace />} />
                <Route path="/policy-analyzer/:id" element={<ProtectedRoute perm="dashboard:view"><PolicyAnalysisDetail /></ProtectedRoute>} />
                <Route path="/forecast" element={<Navigate to="/insights?tab=forecast" replace />} />
                <Route path="/firm-dna" element={<Navigate to="/insights?tab=firm-dna" replace />} />
                <Route path="/radar" element={<Navigate to="/insights?tab=radar" replace />} />
                <Route path="/ask" element={<Navigate to="/studio?tab=ask" replace />} />
                {/* Studio hub — consolidates Ask · Specialists · Meetings · Policy Analyzer */}
                <Route path="/studio" element={<ProtectedRoute perm="dashboard:view"><Studio /></ProtectedRoute>} />
                <Route path="/proposals" element={<Navigate to="/studio?assistant=proposals" replace />} />
                <Route path="/grc" element={<Navigate to="/studio?assistant=grc" replace />} />
                <Route path="/executive" element={<Navigate to="/studio?assistant=executive" replace />} />
                <Route path="/soc" element={<Navigate to="/studio?assistant=soc" replace />} />
                <Route path="/sales" element={<Navigate to="/studio?assistant=sales" replace />} />
                <Route path="/governance" element={<Navigate to="/studio?assistant=governance" replace />} />
                <Route path="/training" element={<Navigate to="/studio?assistant=training" replace />} />
                <Route path="/cloud" element={<Navigate to="/studio?assistant=cloud" replace />} />
                <Route path="/legal" element={<Navigate to="/studio?assistant=legal" replace />} />
                <Route path="/outputs/:id" element={<ProtectedRoute perm="dashboard:view"><OutputDetail /></ProtectedRoute>} />
                <Route path="/workspaces" element={<ProtectedRoute perm="dashboard:view"><Workspaces /></ProtectedRoute>} />
                <Route path="/workspaces/:id" element={<ProtectedRoute perm="dashboard:view"><WorkspaceDetail /></ProtectedRoute>} />
                <Route path="/portal" element={<ProtectedRoute perm="dashboard:view"><WorkspaceDetail /></ProtectedRoute>} />
                <Route path="/clients/:slug" element={<ProtectedRoute perm="dashboard:view"><WorkspaceDetail /></ProtectedRoute>} />
                <Route path="/pipeline" element={<ProtectedRoute perm="dashboard:view"><Pipeline /></ProtectedRoute>} />
                <Route path="/pricing" element={<ProtectedRoute perm="dashboard:view"><Pricing /></ProtectedRoute>} />
                <Route path="/control-library/:id" element={<ProtectedRoute perm="dashboard:view"><ControlDetail /></ProtectedRoute>} />
                <Route path="/frameworks/:id" element={<ProtectedRoute perm="dashboard:view"><FrameworkDetail /></ProtectedRoute>} />
                <Route path="/intelligence" element={<ProtectedRoute perm="dashboard:view"><IntelligenceClients /></ProtectedRoute>} />
                <Route path="/intelligence/org/:id" element={<ProtectedRoute perm="dashboard:view"><CompanyProfile /></ProtectedRoute>} />
                <Route path="/intelligence/:country" element={<ProtectedRoute perm="dashboard:view"><IntelligenceClients /></ProtectedRoute>} />
                <Route path="/review-queue" element={<Navigate to="/knowledge?tab=review" replace />} />
                {/* Deprecated top-level routes — merged into the Admin Console hub */}
                <Route path="/audit" element={<Navigate to="/admin/security-environment?tab=audit" replace />} />
                <Route path="/security" element={<Navigate to="/admin/security-environment?tab=audit" replace />} />
                <Route path="/system" element={<Navigate to="/admin/maintenance" replace />} />
                <Route path="/admin" element={<ProtectedRoute perm="dashboard:view"><AdminConsole /></ProtectedRoute>} />
                <Route path="/admin/:section" element={<ProtectedRoute perm="dashboard:view"><AdminConsole /></ProtectedRoute>} />
                {/* Personal settings moved under Administration for a single place to configure the platform. */}
                <Route path="/settings" element={<Navigate to="/admin/my-account" replace />} />
                <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
